import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LoadedData } from '../domain';
import { loadAppData } from '../data/repository';
import { initializeAds } from '../ads/ad-service';

interface AppContextValue {
  loaded: LoadedData | null;
  loading: boolean;
}

const AppContext = createContext<AppContextValue | null>(null);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [loaded, setLoaded] = useState<LoadedData | null>(null);

  useEffect(() => {
    let active = true;
    loadAppData().then((result) => {
      if (active) setLoaded(result);
    });
    initializeAds().catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => ({ loaded, loading: loaded === null }), [loaded]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppData = (): LoadedData => {
  const context = useContext(AppContext);
  if (!context?.loaded) throw new Error('App data is not ready');
  return context.loaded;
};

export const useAppLoading = (): boolean => {
  const context = useContext(AppContext);
  return context?.loading ?? true;
};
